import React, { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, SelectPickerInput } from "@shared/components";
import { Option } from "@shared/interfaces";
import { useLoader } from "@shared/hooks";

import { actions, selectors } from "../../store";
import { EvalDatasetName } from "../../interfaces";

import "./RunEvalModal.scss";

interface RunEvalModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

// 1 assistant call + ~4 grader calls per case. The planning docs live in the 923-core-context repo, at
// context/engineering/procedure-assistant/phase-20h-web-eval-screens.md §5 — not in this one.
// Moves if the criteria count per case changes — kept as a named constant rather than inline so that
// change has one place to happen.
const VERTEX_CALLS_PER_CASE = 5;

// Deliberately distinct from the API's own 409 message ("An evaluation run is already in progress",
// api/app/shared/constants/errorMessages.ts) — a near-paraphrase would let a modal that ignores
// submitError.isConflict and just renders submitError.message pass this arm by coincidence.
const RUN_IN_PROGRESS_MESSAGE = "Another run is already going. Wait for it to finish, then start a new one.";

const RunEvalModal: FC<RunEvalModalProps> = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();

  const datasets = useSelector(selectors.getEvalDatasets());
  const submitError = useSelector(selectors.getSubmitError());
  const lastSubmittedUuid = useSelector(selectors.getLastSubmittedUuid());

  const [selectedDataset, setSelectedDataset] = useState<EvalDatasetName | null>(null);
  // Gates BOTH store reads below. Reset to false on every mount (a fresh modal instance), which is
  // what actually stops a previous instance's leftover submitError/lastSubmittedUuid from rendering
  // or closing this one — the store's own clear-on-request exists for a narrower, within-instance
  // case (see EvalState.interface.tsx's doc comments on those two fields), not this one.
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { isLoading: isSubmitting } = useLoader({
    name: "SubmitEvalRun",
    actionTypes: useMemo(() => [actions.submitEvalRun], []),
  });

  useEffect(() => {
    dispatch(actions.getEvalDatasets.request());
  }, [dispatch]);

  useEffect(() => {
    if (hasSubmitted && lastSubmittedUuid) {
      onSuccess();
    }
  }, [hasSubmitted, lastSubmittedUuid, onSuccess]);

  const datasetOptions: Option<EvalDatasetName>[] = datasets.map((dataset) => ({
    value: dataset.name,
    label: `${dataset.name} (${dataset.cases} questions)`,
  }));

  const selected = datasets.find((dataset) => dataset.name === selectedDataset);
  const callEstimate = selected ? Math.round(selected.cases * VERTEX_CALLS_PER_CASE) : null;

  const error = hasSubmitted ? submitError : null;
  const isConfirmDisabled = !selectedDataset || isSubmitting;

  const onConfirm = () => {
    if (!selectedDataset) return;
    // Order matters, and is safe specifically because React 18 batches synchronous updates from an
    // event handler (web/package.json's react/react-dom are ^18) — both this state update and the
    // dispatch below land in the same render, so no intermediate render ever observes
    // hasSubmitted=true against the PREVIOUS instance's stale lastSubmittedUuid.
    setHasSubmitted(true);
    dispatch(actions.submitEvalRun.request({ dataset: selectedDataset }));
  };

  return (
    <div className="run-eval-modal">
      <div className="modal-heading">Start evaluation run</div>

      <SelectPickerInput
        labelTitle="Dataset"
        data={datasetOptions}
        value={selectedDataset}
        // SelectPickerInput wraps rsuite's SelectPicker typed as SelectPickerProps<any>
        // (shared/components/Common/SelectPickerInput/SelectPickerInput.tsx), so annotating this
        // callback's param as EvalDatasetName would be an unchecked, unenforced claim rather than a
        // real type — every other call site in this codebase (ProjectForm.tsx, ProcedureForm.tsx,
        // ...) leaves it unannotated for the same reason.
        onChange={(value) => setSelectedDataset(value)}
        searchable={false}
        cleanable={false}
      />

      {selected && callEstimate !== null && (
        <div className="run-eval-modal-cost">
          <p>
            This sends {selected.cases} questions through the assistant and its graders — roughly {callEstimate} AI
            calls — and takes 15-25 minutes to finish.
          </p>
          <p>You can leave this page once it has started.</p>
        </div>
      )}

      {error && (
        <div className="run-eval-modal-error">{error.isConflict ? RUN_IN_PROGRESS_MESSAGE : error.message}</div>
      )}

      <div className="modal-actions">
        {/* Disabled while submitting, not just Confirm — the dispatch already fired the real
           request by this point (submitEvalRun's success/failure is the 202 round-trip, not the
           run itself, so this window is brief), and closing before it resolves would unmount the
           modal before its own success effect can run the list's page-0 refetch — leaving the
           operator with no on-screen confirmation their click did anything. */}
        <Button appearance="subtle" className="subtle auto-width" disabled={isSubmitting} onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" className="accent padding margin-left" disabled={isConfirmDisabled} onClick={onConfirm}>
          Start evaluation run
        </Button>
      </div>
    </div>
  );
};

export default RunEvalModal;
